import React, { useEffect, useState } from "react";
import { Badge, Card, Table } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";

export default function IslamicListTeachTracking() {
  const { is_teachId } = useParams();
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const [context, setContext] = useState(null);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    if (!is_teachId) return;
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-teaching/teachTrackingByTeachId/${is_teachId}`)
      .then(res => {
        setContext(res.data.context);
        setRecords(res.data.records);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [is_teachId, setLoading]);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <React.Fragment>
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <ol className="breadcrumb fs-sm mb-1">
            <li className="breadcrumb-item"><Link to="/dashboard/school-statistic">{t.listTeachTracking.breadHome}</Link></li>
            <li className="breadcrumb-item"><Link to="/islamic/teaching-tracking">{t.teachingTracking.pageTitle}</Link></li>
            <li className="breadcrumb-item active">{t.listTeachTracking.breadCurrent}</li>
          </ol>
          <h4 className="main-title mb-0">{t.listTeachTracking.pageTitle}</h4>
        </div>
      </div>

      {context && (
        <Card className="card-one mb-3">
          <Card.Body className="p-3">
            <div className="d-flex flex-wrap align-items-center gap-3">
              <span className="fw-semibold fs-5 text-primary">{context.isubject_isid}</span>
              <span className="fw-semibold arabic-text">{context.isubject_name}</span>
              <Badge bg="secondary">{t.common.classNo(context.ic_class, context.ir_no)}</Badge>
              {context.is_programCode && <Badge bg="info">{context.is_programCode}</Badge>}
              {context.t_name && (
                <span className="text-secondary small"><i className="ri-user-line me-1"></i>{context.t_name}</span>
              )}
              <span className="text-secondary small ms-auto">
                <i className="ri-book-open-line me-1"></i>{t.common.studyYear(context.year_year)}
              </span>
            </div>
          </Card.Body>
        </Card>
      )}

      <Card className="card-one">
        <Card.Header className="d-flex align-items-center">
          <Card.Title as="h6" className="mb-0">
            {t.listTeachTracking.listTitle}
            {records.length > 0 && (
              <Badge bg="secondary" className="ms-2 fw-normal">{t.listTeachTracking.countTimes(records.length)}</Badge>
            )}
          </Card.Title>
        </Card.Header>
        <Card.Body className="p-0">
          {records.length === 0 ? (
            <div className="text-center text-secondary py-5">
              <i className="ri-file-list-3-line fs-3 d-block mb-2"></i>
              {t.listTeachTracking.noRecords}
            </div>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead className="table-secondary">
                <tr>
                  <th className="text-center" style={{ width: 50 }}>#</th>
                  <th className="text-center" style={{ width: 120 }}>{t.listTeachTracking.colDate}</th>
                  <th className="text-center" style={{ width: 80 }}>{t.listTeachTracking.colTime}</th>
                  <th className="text-center" style={{ width: 70 }}>{t.listTeachTracking.colPeriod}</th>
                  <th>{t.listTeachTracking.colTopic}</th>
                  <th className="text-center" style={{ width: 70 }}>
                    <span className="text-success">{t.listTeachTracking.colPresent}</span>
                  </th>
                  <th className="text-center" style={{ width: 70 }}>
                    <span className="text-danger">{t.listTeachTracking.colAbsent}</span>
                  </th>
                  <th className="text-center" style={{ width: 70 }}>
                    <span className="text-warning">{t.listTeachTracking.colLate}</span>
                  </th>
                  <th className="text-center" style={{ width: 70 }}>
                    <span className="text-info">{t.listTeachTracking.colLeave}</span>
                  </th>
                  <th className="text-center" style={{ width: 70 }}>{t.listTeachTracking.colTotal}</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, idx) => {
                  const total = Number(r.total_students) || 0;
                  return (
                    <tr key={r.itr_id}>
                      <td className="text-center text-muted">{idx + 1}</td>
                      <td className="text-center">{formatDate(r.is_att_date)}</td>
                      <td className="text-center text-muted">{formatTime(r.created_at)}</td>
                      <td className="text-center">{r.period_order || '-'}</td>
                      <td>{r.itr_topic || <span className="text-muted fst-italic">{t.listTeachTracking.noTopic}</span>}</td>
                      <td className="text-center">
                        {total > 0 ? <span className="text-success fw-semibold">{Number(r.presents) || 0}</span> : '-'}
                      </td>
                      <td className="text-center">
                        {total > 0 ? <span className="text-danger fw-semibold">{Number(r.absents) || 0}</span> : '-'}
                      </td>
                      <td className="text-center">
                        {total > 0 ? <span className="text-warning fw-semibold">{Number(r.lates) || 0}</span> : '-'}
                      </td>
                      <td className="text-center">
                        {total > 0 ? <span className="text-info fw-semibold">{Number(r.leaves) || 0}</span> : '-'}
                      </td>
                      <td className="text-center text-muted">{total > 0 ? total : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </React.Fragment>
  );
}
